import { DetailedComplexEnumItem } from '../../utils/complexEnum/DetailedComplexEnumItem'
import { CIMSPageItem } from '../cimsPage/CIMSPageItem'

export class PermissionItem<
  ID extends string,
  Name extends string
> extends DetailedComplexEnumItem<ID, Name> {
  /**
   * @example
   * const description = `Permits a user to ${action} ${objects}`
   * const descriptionWhenActionIsDescription = `Permits user to ${action}`
   */
  description!: string
  /**
   * if the permission is CRUD-like permission, which type is it?
   */
  crud?: 'Create' | 'View' | 'Edit' | 'Remove' | 'Remove & Restore' | undefined
  /**
   * ## Important
   *
   * in the past, id was uppercased at construction
   *
   * since moving to an even stronger type system for complex enums, we had to remove that
   *
   * ___always use uppercased ids___
   *
   * @see {@link DetailedComplexEnumItem.id}
   */
  constructor({
    action,
    id,
    name,
    allowMixedCase,
    crud,
  }: CreatePermissionItem<ID, Name>) {
    super({
      id,
      name,
      description: `Permits a user to ${
        allowMixedCase ? action : action.toLowerCase()
      }`,
    })
    this.crud = crud
  }
}

export interface CreatePermissionItem<ID extends string, Name extends string>
  extends Pick<PermissionItem<ID, Name>, 'name' | 'id' | 'crud'> {
  /**
   * the action associated with the permission
   *
   * used to generate the description
   *
   * @see {@link allowMixedCase}
   * @example
   * const description = `Permits a user to ${action.toLowerCase()}`
   * const mixed = `Permits a user to ${action}`
   */
  action: string
  /**
   * the action shouldn't be converted to lowercase
   */
  allowMixedCase?: true
}

export class BasicPermissionItem<
  ID extends string,
  Name extends string,
  PageID extends string,
  PageName extends string,
  PagePathname extends string,
  ModuleID extends string,
  ModuleName extends string,
  ModulePathname extends string
> extends PermissionItem<ID, Name> {
  /**
   * @example
   * const description = `Permits a user to ${action} ${objects}`
   * const descriptionWhenActionIsDescription = `Permits user to ${action}`
   */
  description!: string
  /**
   * the {@link CIMSPageItem} associated with the permission
   */
  page: CIMSPageItem<
    PageID,
    PageName,
    PagePathname,
    ModuleID,
    ModuleName,
    ModulePathname
  >

  /**
   * __you shouldn't create new permissions outside of the shared package__
   *
   * before invoking a new {@link BasicPermissionItem}, see if there is a common
   * wrapper to make your life easier
   *
   * @see /shared/src/constants/permission/common.ts
   * @param options {@link CreateBasicPermissionItem}
   */
  constructor({
    page,
    ...rest
  }: CreateBasicPermissionItem<
    ID,
    Name,
    PageID,
    PageName,
    PagePathname,
    ModuleID,
    ModuleName,
    ModulePathname
  >) {
    super(rest)
    this.page = page
  }
}

export type CreateBasicPermissionItem<
  ID extends string,
  Name extends string,
  PageID extends string,
  PageName extends string,
  PagePathname extends string,
  ModuleID extends string,
  ModuleName extends string,
  ModulePathname extends string
> = CreatePermissionItem<ID, Name> &
  Pick<
    BasicPermissionItem<
      string,
      string,
      PageID,
      PageName,
      PagePathname,
      ModuleID,
      ModuleName,
      ModulePathname
    >,
    'page'
  >

export class DeveloperPermissionItem<
  ID extends string,
  Name extends string
> extends PermissionItem<ID, Name> {
  /**
   * special permissions for developers
   *
   * @see {@link CreatePermissionItem}
   * @param options {@link CreateDeveloperPermissionItem}
   */
  constructor(options: CreateDeveloperPermissionItem<ID, Name>) {
    super(options)
  }
}

type CreateDeveloperPermissionItem<
  ID extends string,
  Name extends string
> = CreatePermissionItem<ID, Name>
